import { useEffect, useState } from 'react';
import { Empty, Link, Rule, Select, ui } from '../ui';
import { formatBytes } from '@goodbuild/rules/format';
import { isLauncher } from '../is-launcher';
import { deviceName, isRegistered } from '../launcher-device';
import { chooseLibraryRoot, defaultLibraryRoot } from '../launcher-ipc';
import {
  autoUpdate,
  bandwidthCap,
  chunksAtOnce,
  libraryRoot,
  setLibraryRoot,
  setAutoUpdate,
  setBandwidthCap,
  setChunksAtOnce,
  setUpdateCheckMinutes,
  updateCheckMinutes,
  BANDWIDTH_CAP_CHOICES,
  CHUNKS_AT_ONCE_CHOICES,
  CHUNKS_AT_ONCE_DEFAULT,
  UPDATE_CHECK_CHOICES,
  UPDATE_CHECK_DEFAULT,
} from '../launcher-prefs';
import { Field, Notice, Screen, Table } from './shared';

/**
 * Launcher settings.
 *
 * ## The rule this screen is written against
 *
 * **Every control here changes something.** A settings page is the easiest
 * place in a product to lie, because the four settings a desktop launcher is
 * expected to have — where games install, a bandwidth cap, how many downloads
 * at once, whether to update automatically — are half an hour of switches and
 * look completely convincing. A switch that changes nothing is worse than a
 * missing one: the player sets it, believes it, and then spends an evening
 * debugging a problem they think they have already fixed.
 *
 * So this screen offers what something in this repository actually reads, and
 * `Absent` at the bottom names the ones that are not here and says what would
 * have to exist first for each. That table is not an apology — it is the part
 * of the screen a person can act on, because it tells them not to go looking.
 *
 * ## Why signing out is a link
 *
 * `endSession` is called from `routes/account.tsx` and nowhere else, and a test
 * in this package pins it there. That is not ceremony: it clears the stored
 * bearer token *and* ends the server session *and* clears the storefront's
 * hint, and a second caller that forgot one of the three would leave a dead
 * token in storage — which reads as "signed in" to everything until the next
 * request fails. One place to get it right, and the account page is it. The
 * machines list lives on the same page for the same reason, so this points at
 * it rather than growing a second revoke button with a different confirmation.
 */
export function LauncherSettingsRoute(): React.ReactElement {
  /*
   * Registered only in the launcher, and refused here as well — the same pair
   * as `routes/downloads.tsx`, for the same reason. The registration is what
   * keeps a browser from routing here; this is what makes a mistake there
   * render a sentence rather than a page of controls that govern nothing.
   */
  if (!isLauncher()) return <NotOutHere />;
  return <Settings />;
}

function NotOutHere(): React.ReactElement {
  return (
    <Screen title="Launcher" query={{ isPending: false, error: null }}>
      <Empty>
        These settings belong to the launcher. <Link href={'/download'}>Get it here</Link>, or use{' '}
        <Link href="/account">your account page</Link>.
      </Empty>
    </Screen>
  );
}

function Settings(): React.ReactElement {
  return (
    <Screen title="Launcher" query={{ isPending: false, error: null }}>
      <Library />
      <Downloading />
      <Account />
      <Absent />
    </Screen>
  );
}

/**
 * Where games install.
 *
 * **A native folder picker rather than a text field**, and that is the whole
 * reason this waited on a dependency. A path somebody types is a path that is
 * misspelled, relative, on a drive that is not mounted, or inside a directory
 * they cannot write to — and each of those fails after the download rather than
 * before it. A picker returns a folder that exists, and the Rust side then
 * proves it is writable by writing to it, because a directory can exist, report
 * itself writable and still refuse.
 *
 * **Changing it moves nothing, and the screen says so.** Every install record
 * holds the absolute path of the folder its game is actually in, so games
 * already installed keep working exactly where they are. Moving one would mean
 * copying tens of gigabytes and rewriting a record mid-copy, which is the
 * operation most likely to leave somebody with neither copy.
 */
function Library(): React.ReactElement {
  const [chosen, setChosen] = useState(() => libraryRoot());
  const [fallback, setFallback] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The launcher's own folder, so the control can show a real path before
  // anybody has picked one. "The default" is not an answer to "where are my
  // game files".
  useEffect(() => {
    if (!isLauncher()) return;
    void defaultLibraryRoot()
      .then(setFallback)
      .catch(() => {});
  }, []);

  function pick(): void {
    setPicking(true);
    setError(null);

    void chooseLibraryRoot()
      .then((picked) => {
        // Null is a cancel, which should leave the setting exactly as it was.
        if (picked === null) return;
        setLibraryRoot(picked);
        setChosen(libraryRoot());
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : 'That folder cannot be used.');
      })
      .finally(() => setPicking(false));
  }

  function reset(): void {
    setLibraryRoot(null);
    setChosen(null);
    setError(null);
  }

  return (
    <>
      <h2 className={ui.sectionTitle ?? ''}>Where games install</h2>

      <Field label="Games folder">
        <span className={ui.statLabel ?? ''}>
          {chosen ?? fallback ?? 'The launcher’s own folder'}
        </span>
      </Field>

      <div style={{ display: 'flex', gap: 'var(--gb-space-3)', flexWrap: 'wrap' }}>
        <button
          type="button"
          className={ui.buttonSecondary ?? ''}
          disabled={picking}
          onClick={pick}
        >
          {picking ? 'Choosing…' : 'Choose a folder'}
        </button>
        {chosen === null ? null : (
          <button type="button" className={ui.buttonSecondary ?? ''} onClick={reset}>
            Use the default
          </button>
        )}
      </div>

      {error ? <Notice>{error}</Notice> : null}

      <Notice>
        This applies to the next game you install. Games already on this machine stay where they are
        and keep working — the launcher remembers each one’s folder, so nothing has to be moved or
        re-downloaded.
      </Notice>
    </>
  );
}

/**
 * The two settings here that are settings rather than links.
 *
 * **Both are honoured by the Rust side now**, which is a change worth stating
 * because the previous note here said the opposite. The download loop used to
 * be JavaScript, so "chunks at once" was obeyed by the driver and the core's
 * own `with_concurrency` was never called; the loop is `downloader.rs` now, and
 * both numbers are passed to `start_download` and read by the thing that
 * fetches.
 *
 * **The bandwidth cap is new**, and it is here because it stopped being a lie.
 * `DownloadJob` has carried a `bandwidth_cap_bps` field since it was written
 * with nothing reading it, which is exactly the decoration this screen refuses
 * to show. There is a token bucket behind it now — `launcher-core`'s `rate.rs`,
 * shared by every worker so that the cap is on the connection rather than on a
 * lane — so four lanes against 1MB/s together move 1MB/s.
 *
 * Both take effect on the next download rather than on the one in flight,
 * because the values are read once when the job is created. Said on the screen,
 * since a player who changes one mid-download and sees nothing happen has been
 * told nothing by the absence.
 */
function Downloading(): React.ReactElement {
  const [lanes, setLanes] = useState(() => chunksAtOnce());
  const [cap, setCap] = useState(() => bandwidthCap());
  const [checkEvery, setCheckEvery] = useState(() => updateCheckMinutes());
  const [automatic, setAutomatic] = useState(() => autoUpdate());

  function choose(value: string): void {
    const next = Number.parseInt(value, 10);
    setChunksAtOnce(next);
    setLanes(chunksAtOnce());
  }

  function chooseCap(value: string): void {
    setBandwidthCap(value === '' ? null : Number.parseInt(value, 10));
    setCap(bandwidthCap());
  }

  function chooseInterval(value: string): void {
    setUpdateCheckMinutes(Number.parseInt(value, 10));
    setCheckEvery(updateCheckMinutes());
  }

  function chooseAutomatic(value: string): void {
    setAutoUpdate(value === 'on');
    setAutomatic(autoUpdate());
  }

  return (
    <>
      <Rule />
      <h2 className={ui.sectionTitle ?? ''}>Downloading</h2>

      <Field label="Pieces at once">
        <Select
          options={CHUNKS_AT_ONCE_CHOICES.map((value) => ({
            value: String(value),
            label: value === CHUNKS_AT_ONCE_DEFAULT ? `${value} — the default` : String(value),
          }))}
          value={String(lanes)}
          onChange={choose}
          aria-label="How many pieces to download at once"
        />
      </Field>

      <Field label="Speed limit">
        <Select
          options={BANDWIDTH_CAP_CHOICES.map((value) => ({
            value: value === null ? '' : String(value),
            label: value === null ? 'No limit — the default' : `${formatBytes(value)}/s`,
          }))}
          value={cap === null ? '' : String(cap)}
          onChange={chooseCap}
          aria-label="How fast downloads may go"
        />
      </Field>

      <Notice>
        More pieces at once is faster on a good connection and slower on a bad one, because a full
        line makes everything crawl. The cap covers the whole connection, however many pieces are
        running. Both settings apply to the next download, not to one already going.
      </Notice>

      <Rule />
      <h2 className={ui.sectionTitle ?? ''}>Updates</h2>

      <Field label="Check for updates">
        <Select
          options={UPDATE_CHECK_CHOICES.map((value) => ({
            value: String(value),
            label:
              value === UPDATE_CHECK_DEFAULT
                ? `Every ${value} minutes — the default`
                : value < 60
                  ? `Every ${value} minutes`
                  : `Every ${value / 60} hours`,
          }))}
          value={String(checkEvery)}
          onChange={chooseInterval}
          aria-label="How often to check for game updates"
        />
      </Field>

      <Field label="Download updates automatically">
        <Select
          options={[
            { value: 'off', label: 'Ask me — the default' },
            { value: 'on', label: 'Download them' },
          ]}
          value={automatic ? 'on' : 'off'}
          onChange={chooseAutomatic}
          aria-label="Whether updates download without asking"
        />
      </Field>

      <Notice>
        The launcher always checks, and the downloads screen shows what is out of date. Downloading
        is the part that waits for you: an update can be tens of gigabytes, and starting one without
        asking spends your data — and on some connections, your money.
      </Notice>
    </>
  );
}

/** Who this launcher is signed in as, and where the controls for that live. */
function Account(): React.ReactElement {
  return (
    <>
      <Rule />
      <h2 className={ui.sectionTitle ?? ''}>This computer</h2>

      <p className="gb-body">
        The launcher signs in the way a browser does. Signing out, and the list of computers you can
        sign out from, are on <Link href="/account">your account page</Link> — one place, so no half
        of it gets forgotten.
      </p>

      <Registration />
    </>
  );
}

/**
 * Whether this launcher has registered itself, and under what name.
 *
 * Worth showing rather than assuming: registration is deliberately allowed to
 * fail silently — a launcher that cannot register still downloads, installs and
 * plays — so without this the only symptom of a machine that never registered
 * would be its absence from a list on a different page.
 */
function Registration(): React.ReactElement {
  const name = deviceName();

  if (!isRegistered()) {
    return (
      <Notice>
        This machine has not registered itself yet, so it will not appear in that list. It registers
        the first time the downloads screen loads while you are signed in; until then the launcher
        uses your ordinary session, which works and expires sooner.
      </Notice>
    );
  }

  return (
    <Notice>
      This machine is registered as <strong>{name}</strong>. That is the name to look for when
      revoking it, and revoking it there signs this launcher out.
    </Notice>
  );
}

/**
 * The settings that are not here, and why.
 *
 * Written as a table rather than left out silently. Somebody looking for a
 * bandwidth cap will look twice, then look in a preferences file, then ask —
 * and the answer is short enough to print. Each row names the thing that would
 * have to exist first, so it is also the list of what to build next.
 */
function Absent(): React.ReactElement {
  const rows: { setting: string; why: string }[] = [
    {
      setting: 'Count playtime while you play',
      why:
        'The launcher records how long a game ran and never claims you were playing. Activity ' +
        'means input and progression events, which only the game can report — so the minutes ' +
        'that unlock a review, or weigh against a refund, wait on a game-side integration.',
    },
  ];

  return (
    <>
      <Rule />
      <h2 className={ui.sectionTitle ?? ''}>Not here yet</h2>

      <Table head={['Setting', 'Why not']}>
        {rows.map((row) => (
          <tr key={row.setting}>
            <td>{row.setting}</td>
            <td>{row.why}</td>
          </tr>
        ))}
      </Table>
    </>
  );
}
